import { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ShieldCheck, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, Info,
  Briefcase, Users, Factory, Store, Code2, Shirt, UtensilsCrossed, HardHat,
  Stethoscope, GraduationCap, Truck, Plane, Scale, Package, Printer,
  MessageCircle, Calendar, IndianRupee, TrendingUp, Download, RotateCcw,
} from "lucide-react";
import useCanonical from "../hooks/useCanonical";
import useJsonLd from "../hooks/useJsonLd";

// ---------- Question data ----------
const BUSINESS_TYPES = [
  { value: "PROPRIETORSHIP", label: "Sole Proprietorship", Icon: Briefcase },
  { value: "PARTNERSHIP", label: "Partnership Firm", Icon: Users },
  { value: "PVT_LTD", label: "Private Limited Company", Icon: Factory },
  { value: "LLP", label: "LLP (Limited Liability Partnership)", Icon: Scale },
  { value: "FREELANCER", label: "Freelancer / Consultant", Icon: Code2 },
  { value: "NOT_STARTED", label: "Not Yet Started (Planning Stage)", Icon: ShieldCheck },
];

const TURNOVERS = [
  { value: "PRE_REVENUE", label: "Pre-Revenue / Not Started", lakhs: 0 },
  { value: "BELOW_20L", label: "Below ₹20 Lakhs", lakhs: 15 },
  { value: "20_40L", label: "₹20 Lakhs – ₹40 Lakhs", lakhs: 30 },
  { value: "40L_1CR", label: "₹40 Lakhs – ₹1 Crore", lakhs: 70 },
  { value: "1_5CR", label: "₹1 Crore – ₹5 Crore", lakhs: 300 },
  { value: "5_10CR", label: "₹5 Crore – ₹10 Crore", lakhs: 700 },
  { value: "ABOVE_10CR", label: "Above ₹10 Crore", lakhs: 1500 },
];

const EMPLOYEES = [
  { value: "NONE", label: "None (just me)", count: 0 },
  { value: "1_5", label: "1–5 employees", count: 3 },
  { value: "6_10", label: "6–10 employees", count: 8 },
  { value: "11_20", label: "11–20 employees", count: 15 },
  { value: "20_PLUS", label: "20+ employees", count: 25 },
];

const INDUSTRIES = [
  { value: "MANUFACTURING", label: "Manufacturing", Icon: Factory },
  { value: "TRADING", label: "Trading / Wholesale", Icon: Package },
  { value: "RETAIL", label: "Retail / Shop", Icon: Store },
  { value: "IT", label: "IT / Software Services", Icon: Code2 },
  { value: "TEXTILE", label: "Textile / Garments", Icon: Shirt },
  { value: "FOOD", label: "Food / Restaurant / Catering", Icon: UtensilsCrossed },
  { value: "CONSTRUCTION", label: "Construction / Real Estate", Icon: HardHat },
  { value: "HEALTHCARE", label: "Healthcare / Pharmacy", Icon: Stethoscope },
  { value: "EDUCATION", label: "Education / Coaching", Icon: GraduationCap },
  { value: "TRANSPORT", label: "Transport / Logistics", Icon: Truck },
  { value: "TRADE", label: "Export / Import", Icon: Plane },
  { value: "PROFESSIONAL", label: "Professional Services (CA, Lawyer, Architect)", Icon: Scale },
  { value: "OTHER", label: "Other", Icon: Briefcase },
];

const TRADE = [
  { value: "NONE", label: "No" },
  { value: "IMPORT", label: "Import only" },
  { value: "EXPORT", label: "Export only" },
  { value: "BOTH", label: "Both import and export" },
];

// Existing registrations (multi-select)
const EXISTING_REGS = [
  { id: "GST", label: "GST Registration" },
  { id: "MSME", label: "MSME/Udyam Registration" },
  { id: "TAN", label: "PAN / TAN" },
  { id: "PF", label: "PF (EPFO) Registration" },
  { id: "ESI", label: "ESI Registration" },
  { id: "SHOP", label: "Shop & Establishment License" },
  { id: "FSSAI", label: "FSSAI Registration" },
  { id: "IEC", label: "IEC (Import Export Code)" },
  { id: "STARTUP_INDIA", label: "Startup India Recognition" },
  { id: "TRADEMARK", label: "Trademark" },
  { id: "NONE", label: "None of the above" },
];

// Question definitions indexed by key. The `flow` below decides order/skip logic.
const QUESTIONS_BY_KEY = {
  businessType: { key: "businessType", title: "What's your business type?", options: BUSINESS_TYPES, grid: 2 },
  turnover: { key: "turnover", title: "What's your annual turnover?", options: TURNOVERS.filter((t) => t.value !== "PRE_REVENUE"), grid: 2 },
  employees: { key: "employees", title: "How many employees do you have?", options: EMPLOYEES, grid: 2 },
  industry: { key: "industry", title: "What's your industry?", options: INDUSTRIES, grid: 3 },
  trade: { key: "trade", title: "Do you import or export goods/services?", options: TRADE, grid: 2 },
  existing: { key: "existing", title: "What registrations do you already have?", multi: true },
};

const buildFlow = (businessType) => {
  // NOT_STARTED: 4 steps (businessType + employees + industry + trade). Skip turnover and existing.
  if (businessType === "NOT_STARTED") return ["businessType", "employees", "industry", "trade"];
  // Others: full 6-step flow with existing registrations at the end.
  return ["businessType", "turnover", "employees", "industry", "trade", "existing"];
};

// ---------- Compliance Engine ----------
const computeRoadmap = (a) => {
  const bt = a.businessType;
  const isNotStarted = bt === "NOT_STARTED";
  const turnoverLakhs = TURNOVERS.find((t) => t.value === a.turnover)?.lakhs || 0;
  const empCount = EMPLOYEES.find((e) => e.value === a.employees)?.count || 0;
  const ind = a.industry;
  const hasExport = a.trade === "EXPORT" || a.trade === "BOTH";
  const hasImport = a.trade === "IMPORT" || a.trade === "BOTH";
  const hasTrade = hasExport || hasImport;
  const isIncorporated = bt === "PVT_LTD" || bt === "LLP";

  const existing = Array.isArray(a.existing) ? a.existing : [];
  const hasExisting = (id) => existing.includes(id);

  const mandatory = [], recommended = [], optional = [];

  // ---------- GST ----------
  if (isNotStarted) {
    // Not started → GST is recommended, not mandatory
    recommended.push({
      id: "GST",
      title: "GST Registration",
      note: "Recommended once your turnover crosses ₹20 Lakhs. Mandatory for inter-state supply, e-commerce, and import/export.",
      link: "/gst-registration",
    });
  } else if (turnoverLakhs > 20 || hasTrade || isIncorporated) {
    mandatory.push({
      id: "GST",
      title: "GST Registration",
      note: "Penalty for operating without GST: ₹10,000 or 100% of tax due, whichever is higher.",
      link: "/gst-registration",
    });
  }

  // ---------- MSME ----------
  if (turnoverLakhs < 25000 && ["PROPRIETORSHIP", "PARTNERSHIP", "PVT_LTD", "LLP"].includes(bt))
    mandatory.push({ id: "MSME", title: "MSME (Udyam) Registration", note: "Unlocks priority lending, government tender preference, and subsidy schemes.", link: "/business-registration" });

  // ---------- EPFO / ESI / PT (employee-based) ----------
  if (empCount >= 20)
    mandatory.push({ id: "PF", title: "EPFO (Provident Fund) Registration", note: "Mandatory for establishments with 20+ employees. Penalty: ₹5,000–₹25,000 + imprisonment." });
  if (empCount >= 10)
    mandatory.push({ id: "ESI", title: "ESI Registration", note: "Mandatory for establishments with 10+ employees where wages are below ₹21,000/month." });
  if (empCount >= 1)
    mandatory.push({ title: "Professional Tax Registration", note: "Employer must deduct and remit. Penalty: ₹5/day for late payment (Tamil Nadu)." });

  // ---------- Shop & Establishment / FSSAI ----------
  if (["RETAIL", "FOOD", "HEALTHCARE", "EDUCATION"].includes(ind))
    mandatory.push({ id: "SHOP", title: "Shop & Establishment License", note: "Required within 30 days of starting business." });
  if (ind === "FOOD")
    mandatory.push({ id: "FSSAI", title: "FSSAI Registration", note: "Operating without FSSAI: ₹5 Lakh fine + 6 months imprisonment." });

  // ---------- IEC / LUT / TDS / ROC ----------
  if (hasTrade)
    mandatory.push({ id: "IEC", title: "IEC (Import Export Code)", note: "Cannot import/export without IEC. Free registration on DGFT portal." });
  if (hasExport)
    mandatory.push({ title: "LUT (Letter of Undertaking)", note: "File annually on GST portal. Without LUT, you pay IGST on exports." });
  if (empCount >= 1 || turnoverLakhs > 100)
    mandatory.push({ id: "TAN", title: "TDS Registration (TAN)", note: "Required for salary, rent above ₹2.4L/year, contractor payments above ₹30,000.", link: "/tds-filing" });
  if (isIncorporated)
    mandatory.push({ title: "ROC Annual Filing", note: "Annual return + financial statements. Late fee: ₹100/day. Strike-off risk after 2 years.", link: "/business-registration" });

  // ---------- Recommended ----------
  if (isNotStarted)
    recommended.push({ id: "STARTUP_INDIA", title: "Startup India Recognition", note: "Tax exemption for 3 years, self-certification, fast-track patent filing." });
  if (turnoverLakhs > 40)
    recommended.push({ id: "TRADEMARK", title: "Trademark Registration", note: "Protect your brand name. Cost: ₹4,500 government fee. Processing: 6–12 months." });
  if ((ind === "MANUFACTURING" || ind === "IT") && turnoverLakhs > 100)
    recommended.push({ title: "ISO 9001 Certification", note: "Mandatory for many government tenders. Improves credibility with large clients." });
  if (isIncorporated)
    recommended.push({ title: "Digital Signature Certificate (DSC)", note: "Required for ROC filing, GST registration and e-tendering." });

  // ---------- Optional ----------
  optional.push({ title: "Annual Tax Planning Advisory", note: "Save 10–30% on taxes through legal planning. Best done before March 31 each year.", link: "/itr-filing" });
  if (turnoverLakhs > 500)
    optional.push({ title: "Internal Audit Setup", note: "Reduces compliance risk and strengthens financial controls." });

  // ---------- Decorate items with completed status from existing registrations ----------
  const decorate = (arr) => arr.map((item) => ({
    ...item,
    completed: item.id ? hasExisting(item.id) : false,
  }));
  const mandatoryD = decorate(mandatory);
  const recommendedD = decorate(recommended);
  const optionalD = decorate(optional);

  const missingMandatory = mandatoryD.filter((i) => !i.completed).length;
  const totalMandatory = mandatoryD.length;
  const completedMandatory = totalMandatory - missingMandatory;

  // ---------- Risk Score ----------
  let riskLevel, riskLabel, riskColor, penaltyEstimate, fullyCompliant = false;
  if (totalMandatory > 0 && missingMandatory === 0) {
    fullyCompliant = true;
    riskLevel = "COMPLIANT"; riskLabel = "FULLY COMPLIANT"; riskColor = "green";
    penaltyEstimate = "₹0 — Great job!";
  } else if (missingMandatory >= 5) {
    riskLevel = "HIGH"; riskLabel = "HIGH RISK"; riskColor = "red"; penaltyEstimate = "₹2,00,000+/year";
  } else if (missingMandatory >= 3) {
    riskLevel = "MEDIUM"; riskLabel = "MEDIUM RISK"; riskColor = "amber"; penaltyEstimate = "₹50,000–₹2,00,000/year";
  } else {
    riskLevel = "LOW"; riskLabel = "LOW RISK"; riskColor = "green"; penaltyEstimate = "Below ₹50,000/year";
  }

  // ---------- Calendar ----------
  const hasGSTItem = [...mandatoryD, ...recommendedD].some((x) => x.id === "GST");
  const hasPF = mandatoryD.some((x) => x.id === "PF");
  const hasESI = mandatoryD.some((x) => x.id === "ESI");
  const hasTDS = mandatoryD.some((x) => x.id === "TAN");
  const hasROC = isIncorporated;
  const hasPT = empCount >= 1;

  const monthly = [];
  if (hasGSTItem) {
    monthly.push({ task: "GSTR-1 (Outward supplies)", deadline: "11th of every month" });
    monthly.push({ task: "GSTR-3B (Summary + tax payment)", deadline: "20th of every month" });
  }
  if (hasPF) monthly.push({ task: "PF Payment", deadline: "15th of every month" });
  if (hasESI) monthly.push({ task: "ESI Payment", deadline: "15th of every month" });

  const quarterly = [];
  if (hasTDS) quarterly.push({ task: "TDS Return", deadline: "31 Jul, 31 Oct, 31 Jan, 31 May" });
  if (turnoverLakhs > 0 && turnoverLakhs < 150 && hasGSTItem) quarterly.push({ task: "GST (Composition Scheme)", deadline: "18th of month after quarter" });

  const annually = [{ task: "Income Tax Return (ITR)", deadline: "31st July" }];
  if (hasGSTItem) annually.push({ task: "GST Annual Return (GSTR-9)", deadline: "31st December" });
  if (hasROC) annually.push({ task: "ROC Annual Return", deadline: "30th September" });
  if (hasExport) annually.push({ task: "LUT Filing", deadline: "31st March" });
  if (hasPT) annually.push({ task: "Professional Tax Annual", deadline: "As per Tamil Nadu rules" });

  // ---------- Cost ----------
  let base = [8000, 15000];
  if (turnoverLakhs > 40 && turnoverLakhs <= 100) base = [15000, 30000];
  else if (turnoverLakhs > 100 && turnoverLakhs <= 500) base = [30000, 60000];
  else if (turnoverLakhs > 500) base = [60000, 150000];

  let add = 0;
  if (hasPF) add += 5000;
  if (hasESI) add += 3000;
  if (hasTDS) add += 8000;
  if (bt === "PVT_LTD") add += 10000;
  if (bt === "LLP") add += 8000;

  const govMin = Math.round(base[0] * 0.25);
  const govMax = Math.round(base[1] * 0.3);
  const profMin = base[0] - govMin + add;
  const profMax = base[1] - govMax + add;
  const totalMin = govMin + profMin;
  const totalMax = govMax + profMax;

  const penaltyRisk = fullyCompliant ? 0 : (missingMandatory >= 5 ? 200000 : missingMandatory >= 3 ? 100000 : 30000);
  const midTotal = (totalMin + totalMax) / 2;
  const multiplier = Math.max(1, Math.round(penaltyRisk / Math.max(midTotal, 1)));

  return {
    mandatory: mandatoryD, recommended: recommendedD, optional: optionalD,
    missingMandatory, completedMandatory, totalMandatory, fullyCompliant,
    riskLevel, riskLabel, riskColor, penaltyEstimate,
    filingsCount: monthly.length * 12 + quarterly.length * 4 + annually.length,
    calendar: { monthly, quarterly, annually },
    cost: { govMin, govMax, profMin, profMax, totalMin, totalMax, penaltyRisk, multiplier },
  };
};

const formatINR = (n) => Math.round(n).toLocaleString("en-IN");

const labelFor = (list, val) => list.find((x) => x.value === val)?.label || "—";

// ---------- Launch Roadmap (for NOT_STARTED users) ----------
const REGULATED_INDUSTRIES = ["FOOD", "HEALTHCARE", "CONSTRUCTION", "MANUFACTURING", "TRANSPORT"];
const WA_BASE = "https://wa.me/918940988776";

const computeLaunchRoadmap = (a) => {
  const ind = a.industry;
  const hasExport = a.trade === "EXPORT" || a.trade === "BOTH";
  const hasImport = a.trade === "IMPORT" || a.trade === "BOTH";
  const hasTrade = hasExport || hasImport;
  const team = a.employees; // NONE / 1_5 / 6_10 / 11_20 / 20_PLUS
  const isSolo = team === "NONE";
  const isRegulated = REGULATED_INDUSTRIES.includes(ind);
  const teamLabel = labelFor(EMPLOYEES, team);
  const tradeLabel = labelFor(TRADE, a.trade);
  const industryLabel = labelFor(INDUSTRIES, ind);

  const phases = [];

  // ---------- Phase 1 (always) ----------
  phases.push({
    title: "Phase 1 — Before You Start",
    subtitle: "Week 1–2",
    items: [
      { title: "Choose Your Business Structure", note: "The right structure affects your taxes, liability, and compliance burden. We help you choose between Proprietorship, Partnership, LLP, or Pvt Ltd.", link: "/business-registration", linkLabel: "Compare Business Structures" },
      { title: "Get PAN Card", note: "Mandatory for all business registrations, bank accounts, and tax filings. Apply online at NSDL or UTIITSL." },
      { title: "Open Business Bank Account (Current Account)", note: "Keep personal and business finances separate from Day 1. Required for GST registration and loan applications." },
      { title: "Register Your Business", note: "Proprietorship: Trade name at local body. Partnership: Register partnership deed. Pvt Ltd: Incorporate with MCA (7–10 days).", link: "/business-registration", linkLabel: "View Business Registration Services" },
    ],
  });

  // ---------- Phase 2 (conditional) ----------
  const p2 = [
    { title: "MSME / Udyam Registration", note: "Free. No turnover requirement. Register from Day 1. Unlocks priority lending, tender preference, subsidies." },
  ];
  if (!isSolo) p2.push({
    title: "Professional Tax Registration (Tamil Nadu)",
    note: "Register as employer when you hire employees. Deduct from salaries and remit to state government. Penalty: ₹5/day for late registration.",
  });
  p2.push({ title: "Trade License / Local Body Permission", note: "Required from your municipal corporation or panchayat before operating commercially." });

  // Industry-specific
  if (ind === "FOOD") {
    p2.push({ title: "FSSAI Registration", note: "MANDATORY before selling any food product. Fine: ₹5 Lakh + 6 months imprisonment.", externalLink: `${WA_BASE}?text=Hi%2C%20I%20need%20FSSAI%20registration%20for%20my%20food%20business`, linkLabel: "Need help? WhatsApp us" });
    p2.push({ title: "Fire Safety NOC", note: "Required for restaurants, cafes, and food courts. Apply at local fire department." });
    p2.push({ title: "Health Trade License", note: "Issued by local municipal health department. Mandatory for food handling establishments." });
  } else if (ind === "HEALTHCARE") {
    p2.push({ title: "Drug License (State Drug Authority)", note: "Mandatory for pharmacy, medical store, or any business selling medicines." });
    p2.push({ title: "Clinical Establishment Registration", note: "Required under Clinical Establishments Act for clinics, labs, and hospitals." });
  } else if (ind === "PROFESSIONAL") {
    p2.push({ title: "Professional Council Registration", note: "CA: Register with ICAI and obtain COP. Lawyer: Enroll with State Bar Council. Architect: Register with Council of Architecture. MANDATORY before starting practice." });
    p2.push({ title: "Professional Indemnity Insurance", note: "Recommended for all professionals. Covers claims arising from professional negligence." });
  } else if (ind === "MANUFACTURING") {
    p2.push({ title: "Factory License", note: "Required under Factories Act if you employ 10+ workers (with power) or 20+ (without power)." });
    p2.push({ title: "Pollution Control Board NOC", note: "Required before setting up any manufacturing unit. Apply at Tamil Nadu Pollution Control Board." });
  } else if (ind === "CONSTRUCTION") {
    p2.push({ title: "RERA Registration", note: "Mandatory for all real estate projects. Register at TNRERA before advertising or selling." });
    p2.push({ title: "Building Plan Approval", note: "Required from local planning authority before starting construction." });
  } else if (ind === "EDUCATION") {
    p2.push({ title: "Trust / Society Registration", note: "Required if operating as a non-profit educational institution." });
    p2.push({ title: "Affiliation / Board Recognition", note: "Apply to relevant education board for formal recognition." });
  } else if (ind === "TRANSPORT") {
    p2.push({ title: "Transport License (RTO)", note: "Required for commercial vehicle operations. Apply at Regional Transport Office." });
    p2.push({ title: "National Permit", note: "Required for interstate goods transport. Apply through Vahan portal." });
  } else if (ind === "TEXTILE") {
    p2.push({ title: "Textile Committee Registration", note: "Optional but beneficial for export-oriented textile businesses. Provides testing and certification services." });
  } else if (ind === "IT") {
    p2.push({ title: "STPI / SEZ Registration", note: "Optional. Provides export benefits and tax incentives for IT companies." });
  } else if (ind === "TRADING") {
    p2.push({ title: "Shops & Commercial Establishment License", note: "Mandatory for all trading businesses. Apply within 30 days of starting." });
  } else if (ind === "RETAIL") {
    p2.push({ title: "Shop & Establishment License", note: "Required within 30 days of starting. Apply at your local labour department." });
    p2.push({ title: "Legal Metrology License", note: "Required if selling packaged goods by weight or measure. Apply at Legal Metrology Department." });
  } else if (ind === "TRADE") {
    p2.push({ title: "IEC Registration", note: "Mandatory. Free on DGFT portal." });
    p2.push({ title: "AD Code Registration", note: "Register with your bank for forex transactions." });
  }
  phases.push({ title: "Phase 2 — Essential Registrations", subtitle: "Week 2–4", items: p2 });

  // ---------- Phase 3 (always, milestone-based) ----------
  phases.push({
    title: "Phase 3 — When Turnover Crosses ₹20 Lakhs",
    subtitle: "Milestone-based — plan ahead",
    milestone: true,
    items: [
      {
        title: "GST Registration",
        note: "MANDATORY when annual turnover exceeds ₹20 Lakhs (₹10 Lakhs in NE/hill states). Voluntary registration available earlier — useful for claiming Input Tax Credit. Penalty without GST: ₹10,000 or 100% of tax due.",
        links: [
          { label: "View GST Registration Services", to: "/gst-registration" },
          { label: "Download GST Notice Response Template", to: "/gst-notice-response-template" },
        ],
      },
      { title: "TAN Registration (for TDS)", note: "Required when you pay salaries, rent above ₹2.4L/year, or contractor payments above ₹30,000. Apply online at NSDL.", link: "/tds-filing", linkLabel: "View TDS Filing Services" },
      { title: "Accounting & Bookkeeping Setup", note: "Maintain books from Day 1. Required for GST returns, ITR filing, bank loans, and investor discussions. Use Tally, Zoho Books, or similar software." },
    ],
  });

  // ---------- Phase 4 (as you grow) ----------
  const p4 = [
    {
      title: "Income Tax Return Filing (Annual)",
      note: "File ITR every year by July 31. Mandatory even if income is below taxable limit. Penalty for late filing: ₹5,000 (₹1,000 if income < ₹5L).",
      links: [
        { label: "View ITR Filing Services", to: "/itr-filing" },
        { label: "Download ITR Checklist", to: "/itr-checklist" },
        { label: "Calculate your tax", to: "/income-tax-calculator" },
      ],
    },
    {
      title: "GST Return Filing (Monthly/Quarterly)",
      note: "After GST registration: File GSTR-1 by 11th and GSTR-3B by 20th every month. Late fee: ₹50/day. Total penalty for 1 year missed returns: ₹18,000+.",
      futureLabel: "After GST registration",
      links: [
        { label: "View GST Return Filing Services", to: "/gst-return-filing" },
        { label: "Download GST Reconciliation Worksheet", to: "/gst-reconciliation-worksheet" },
        { label: "Calculate GST late fee", to: "/gst-penalty-calculator" },
        { label: "Calculate GST interest", to: "/gst-interest-calculator" },
      ],
    },
  ];
  if (team === "20_PLUS")
    p4.push({ title: "PF (EPFO) Registration", note: "Mandatory for 20+ employees. Employer contributes 12% of basic salary. Penalty for non-registration: ₹5,000–₹25,000 + imprisonment." });
  if (team === "11_20" || team === "20_PLUS")
    p4.push({ title: "ESI Registration", note: "Mandatory for 10+ employees where wages are below ₹21,000/month. Employer contributes 3.25% of wages." });
  p4.push({ title: "Startup India Recognition", note: "3-year income tax exemption (Section 80-IAC), self-certification for 6 labour laws and 3 environmental laws, fast-track patent filing. Apply within 10 years of incorporation. Free." });
  if (!isSolo)
    p4.push({ title: "Employee Insurance (Group Health)", note: "Attract and retain talent with group health insurance. Tax deductible business expense. Mandatory welfare measure for growing teams.", link: "/health-insurance-advisory", linkLabel: "View Health Insurance Advisory" });
  p4.push({ title: "Trademark Registration", note: "Protect your brand name before someone else does. Government fee: ₹4,500. Processing: 6–12 months. The longer you wait, the harder it gets." });
  phases.push({ title: "Phase 4 — As You Grow", subtitle: "Ongoing compliance", items: p4 });

  // ---------- Phase 5 (import/export only) ----------
  if (hasTrade) {
    phases.push({
      title: "Phase 5 — Import / Export Setup",
      subtitle: "Required for international trade",
      items: [
        { title: "IEC (Import Export Code)", note: "Free registration on DGFT portal. Cannot import/export without IEC. Processing: 1–2 days online." },
        { title: "LUT (Letter of Undertaking)", note: "File annually on GST portal. Required for zero-rated exports (no IGST on exports). Must be filed before first export of each FY." },
        { title: "AD Code Registration", note: "Register Authorized Dealer Code with your bank. Required for receiving foreign currency payments. One-time process per bank account." },
        { title: "RCMC (Registration Cum Membership Certificate)", note: "Required for availing export benefits and incentives. Register with relevant Export Promotion Council." },
      ],
    });
  }

  // ---------- Totals ----------
  const totalSteps = phases.reduce((n, p) => n + p.items.length, 0);

  // ---------- Cost Boxes ----------
  // Government fees
  let govMin, govMax;
  if (isSolo && !isRegulated) { govMin = 1000; govMax = 3000; }
  else if (isRegulated) { govMin = 3000; govMax = 8000; }
  else { govMin = 2000; govMax = 5000; }
  if (hasTrade) { govMin += 2000; govMax += 2000; }

  // Professional fees
  let profMin, profMax;
  if (isSolo && !isRegulated) { profMin = 5000; profMax = 10000; }
  else if (isRegulated) { profMin = 12000; profMax = 20000; }
  else { profMin = 8000; profMax = 15000; }
  if (hasTrade) { profMin += 5000; profMax += 5000; }

  // Annual compliance (future — after GST + TDS etc.)
  let annualMin = 8000, annualMax = 15000;
  // We assume they'll eventually hit GST and hire TDS/team → show the range
  annualMin += 6000; annualMax += 12000; // GST
  if (!isSolo) { annualMin += 5000; annualMax += 8000; } // TDS
  if (team === "11_20" || team === "20_PLUS") { annualMin += 5000; annualMax += 10000; } // PF/ESI

  // Penalty risk without compliance
  let penaltyMin, penaltyMax;
  if (isSolo && !isRegulated) { penaltyMin = 50000; penaltyMax = 100000; }
  else if (isRegulated) { penaltyMin = 100000; penaltyMax = 300000; }
  else { penaltyMin = 200000; penaltyMax = 500000; }

  const totalSetupMin = govMin + profMin;
  const totalSetupMax = govMax + profMax;
  const midAnnual = (annualMin + annualMax) / 2;
  const midPenalty = (penaltyMin + penaltyMax) / 2;
  const multiplier = Math.max(2, Math.round(midPenalty / Math.max(midAnnual, 1)));

  // Timeline
  let timeline;
  if (isSolo && !isRegulated) timeline = "1–2 weeks";
  else if (isSolo && isRegulated) timeline = "2–3 weeks";
  else if (hasTrade) timeline = "3–4 weeks";
  else timeline = "2–4 weeks";

  // ---------- Annual Calendar (future) ----------
  const calendar = [{ task: "ITR Filing", deadline: "31st July every year", always: true }];
  const afterGST = [
    { task: "GSTR-1", deadline: "11th of every month" },
    { task: "GSTR-3B", deadline: "20th of every month" },
    { task: "GSTR-9 (Annual)", deadline: "31st December" },
  ];
  calendar.push(...afterGST.map((x) => ({ ...x, labelTag: "After GST Registration" })));
  if (!isSolo) {
    calendar.push({ task: "TDS Quarterly Returns", deadline: "31 Jul, Oct, Jan, May" });
    calendar.push({ task: "Professional Tax", deadline: "Monthly/Half-yearly (state rules)" });
  }
  if (team === "20_PLUS") {
    calendar.push({ task: "PF Payment", deadline: "15th of every month" });
    calendar.push({ task: "PF Annual Return", deadline: "30th November" });
  }
  if (team === "11_20" || team === "20_PLUS") {
    calendar.push({ task: "ESI Payment", deadline: "15th of every month" });
  }
  if (hasTrade) {
    calendar.push({ task: "LUT Filing", deadline: "Before 31st March every year" });
  }

  return {
    phases,
    totalSteps,
    remainingSteps: totalSteps,
    timeline,
    cost: { govMin, govMax, profMin, profMax, annualMin, annualMax, penaltyMin, penaltyMax, totalSetupMin, totalSetupMax, multiplier },
    setupCost: { govMin, govMax, profMin, profMax, totalMin: totalSetupMin, totalMax: totalSetupMax },
    penaltyRiskNoCompliance: penaltyMin,
    calendar,
    meta: { industryLabel, teamLabel, tradeLabel, hasTrade, isRegulated, isSolo },
  };
};

// ---------- Option Card ----------
const OptionCard = ({ option, selected, onClick }) => {
  const Icon = option.Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-xl border-2 px-5 py-4 transition-all flex items-center gap-3 ${
        selected
          ? "border-gold bg-gold/10 shadow-[0_0_0_4px_rgba(212,175,55,0.1)]"
          : "border-white/10 bg-white/5 hover:border-gold/40 hover:bg-white/10"
      }`}
      data-testid={`option-${option.value}`}
    >
      {Icon && (
        <span className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${selected ? "bg-gold text-navy" : "bg-white/10 text-gold"}`}>
          <Icon size={20} />
        </span>
      )}
      <span className={`text-sm md:text-base font-medium ${selected ? "text-white" : "text-slate-200"}`}>{option.label}</span>
      {selected && <CheckCircle2 className="ml-auto text-gold flex-shrink-0" size={20} />}
    </button>
  );
};

// ---------- Main Page ----------
const ComplianceRoadmapPage = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const resultsRef = useRef(null);

  useCanonical("/compliance-roadmap");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Business Compliance Roadmap",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "All",
    "description": "Free tool to generate a personalised business compliance roadmap in 60 seconds. Covers GST, MSME, PF, ESI, TDS, ROC, FSSAI and more.",
    "url": "https://wintrustsolutions.com/compliance-roadmap",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
  });

  // Dynamic flow: skip turnover for NOT_STARTED; always include existing registrations as last step.
  const flow = useMemo(() => buildFlow(answers.businessType), [answers.businessType]);
  const totalSteps = flow.length;
  const LOADING_STEP = totalSteps;
  const RESULTS_STEP = totalSteps + 1;

  const isWizard = step < LOADING_STEP;
  const isLoading = step === LOADING_STEP;
  const isResults = step === RESULTS_STEP;

  const isNotStarted = answers.businessType === "NOT_STARTED";
  const roadmap = useMemo(
    () => (isResults && !isNotStarted ? computeRoadmap(answers) : null),
    [isResults, isNotStarted, answers]
  );
  const launch = useMemo(
    () => (isResults && isNotStarted ? computeLaunchRoadmap(answers) : null),
    [isResults, isNotStarted, answers]
  );

  useEffect(() => {
    if (isLoading) {
      const t = setTimeout(() => setStep(RESULTS_STEP), 1800);
      return () => clearTimeout(t);
    }
  }, [isLoading, RESULTS_STEP]);

  useEffect(() => {
    if (isResults && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isResults]);

  const currentKey = isWizard ? flow[step] : null;
  const currentQ = currentKey ? QUESTIONS_BY_KEY[currentKey] : null;
  const currentAnswer = currentKey ? answers[currentKey] : null;
  const canProceed = currentQ?.multi
    ? Array.isArray(currentAnswer) && currentAnswer.length > 0
    : !!currentAnswer;

  const selectOption = (key, value) => {
    setAnswers((a) => {
      const next = { ...a, [key]: value };
      // If business type changes to/from NOT_STARTED, auto-set turnover
      if (key === "businessType" && value === "NOT_STARTED") next.turnover = "PRE_REVENUE";
      if (key === "businessType" && value !== "NOT_STARTED" && a.turnover === "PRE_REVENUE") delete next.turnover;
      return next;
    });
  };

  const toggleMulti = (key, id) => {
    setAnswers((a) => {
      const curr = Array.isArray(a[key]) ? a[key] : [];
      let next;
      if (id === "NONE") {
        // Selecting "None" clears everything and sets only NONE.
        next = curr.includes("NONE") ? [] : ["NONE"];
      } else {
        // Selecting any real option removes NONE and toggles this id.
        const without = curr.filter((x) => x !== "NONE");
        next = without.includes(id) ? without.filter((x) => x !== id) : [...without, id];
      }
      return { ...a, [key]: next };
    });
  };

  const goNext = () => setStep((s) => s + 1);
  const goBack = () => setStep((s) => Math.max(0, s - 1));
  const restart = () => { setStep(0); setAnswers({}); };

  const whatsappUrl = launch
    ? `https://wa.me/918940988776?text=Hi%20WinTrust%20Solutions%2C%0A%0A` +
      `I%20am%20planning%20to%20start%20a%20${encodeURIComponent(labelFor(INDUSTRIES, answers.industry))}%20business%20in%20Coimbatore.%0A` +
      `Employees%3A%20${encodeURIComponent(labelFor(EMPLOYEES, answers.employees))}%0A%0A` +
      `I%20need%20help%20with%20all%20registrations%20and%20compliance%20setup.`
    : roadmap
    ? `https://wa.me/918940988776?text=Hi%20WinTrust%20Solutions%2C%0A%0AI%20used%20the%20Compliance%20Roadmap.%0A` +
      `Business%20Type%3A%20${encodeURIComponent(labelFor(BUSINESS_TYPES, answers.businessType))}%0A` +
      `Turnover%3A%20${encodeURIComponent(labelFor(TURNOVERS, answers.turnover))}%0A` +
      `Employees%3A%20${encodeURIComponent(labelFor(EMPLOYEES, answers.employees))}%0A` +
      `Industry%3A%20${encodeURIComponent(labelFor(INDUSTRIES, answers.industry))}%0A` +
      `Missing%20mandatory%20items%3A%20${roadmap.missingMandatory}%0A%0A` +
      `I%20need%20help%20with%20compliance.`
    : "";

  return (
    <>
      <Helmet>
        <title>Business Compliance Roadmap — Find What Your Business Needs in 60 Seconds | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Free business compliance roadmap for Coimbatore businesses. Answer 5 questions, get your personalised list of mandatory registrations, filings and deadlines." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4 no-print">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">Compliance Roadmap</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-3" data-testid="roadmap-h1">
            What Does YOUR Business Actually Need?
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            Answer 5 questions. Get your personalised compliance roadmap instantly.
          </p>
        </div>
      </section>

      {/* Wizard / Loading / Results */}
      <section className="bg-navy pb-16 min-h-[60vh]">
        <div className="container-custom">
          {/* Questionnaire */}
          {isWizard && currentQ && (
            <div className="max-w-3xl mx-auto" data-testid="wizard">
              {/* Progress */}
              <div className="mb-8 no-print">
                <div className="flex items-center justify-between mb-2 text-xs text-slate-400 font-medium">
                  <span data-testid="progress-label">Step {step + 1} of {totalSteps}</span>
                  <span>{Math.round(((step + 1) / totalSteps) * 100)}% complete</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gold transition-all duration-500 rounded-full"
                    style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
                    data-testid="progress-bar"
                  />
                </div>
              </div>

              {/* Question */}
              <h2 className="text-2xl md:text-3xl font-semibold text-white font-heading mb-2 text-center" data-testid="question-title">
                {currentQ.title}
              </h2>
              {currentQ.multi && (
                <p className="text-slate-400 text-sm text-center mb-6">Select all that apply</p>
              )}
              {!currentQ.multi && <div className="mb-6" />}

              {/* Single-select grid */}
              {!currentQ.multi && (
                <div className={`grid gap-3 ${currentQ.grid === 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
                  {currentQ.options.map((opt) => (
                    <OptionCard
                      key={opt.value}
                      option={opt}
                      selected={answers[currentQ.key] === opt.value}
                      onClick={() => selectOption(currentQ.key, opt.value)}
                    />
                  ))}
                </div>
              )}

              {/* Multi-select (existing registrations) */}
              {currentQ.multi && (
                <div className="grid gap-3 md:grid-cols-2" data-testid="multi-select">
                  {EXISTING_REGS.map((reg) => {
                    const selected = Array.isArray(answers.existing) && answers.existing.includes(reg.id);
                    const isNone = reg.id === "NONE";
                    return (
                      <button
                        key={reg.id}
                        type="button"
                        onClick={() => toggleMulti("existing", reg.id)}
                        className={`w-full text-left rounded-xl border-2 px-5 py-4 transition-all flex items-center gap-3 ${
                          selected
                            ? "border-gold bg-gold/10 shadow-[0_0_0_4px_rgba(212,175,55,0.1)]"
                            : "border-white/10 bg-white/5 hover:border-gold/40 hover:bg-white/10"
                        } ${isNone ? "md:col-span-2" : ""}`}
                        data-testid={`existing-${reg.id}`}
                      >
                        <span className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center ${
                          selected ? "bg-gold border-gold text-navy" : "border-slate-400 bg-transparent"
                        }`}>
                          {selected && <CheckCircle2 size={18} />}
                        </span>
                        <span className={`text-sm md:text-base font-medium ${selected ? "text-white" : "text-slate-200"} ${isNone ? "italic" : ""}`}>
                          {reg.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Nav buttons */}
              <div className="mt-8 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={step === 0}
                  className="flex items-center gap-2 px-5 py-3 rounded-lg text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  data-testid="wizard-back-btn"
                >
                  <ArrowLeft size={18} /> Back
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!canProceed}
                  className="flex items-center gap-2 bg-gold hover:bg-gold/90 text-navy font-semibold px-6 py-3 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed shadow-md transition-colors"
                  data-testid="wizard-next-btn"
                >
                  {step === totalSteps - 1 ? "Generate Roadmap" : "Next"} <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="max-w-2xl mx-auto text-center py-16" data-testid="roadmap-loading">
              <div className="relative inline-flex">
                <div className="absolute inset-0 rounded-full bg-gold/20 animate-ping"></div>
                <div className="relative w-24 h-24 rounded-full bg-gold/10 border-2 border-gold flex items-center justify-center">
                  <ShieldCheck className="text-gold animate-pulse" size={48} />
                </div>
              </div>
              <p className="text-white text-xl font-semibold mt-8 font-heading">Generating your roadmap...</p>
              <p className="text-slate-400 text-sm mt-2">Analysing 20+ compliance rules for your business</p>
            </div>
          )}

          {/* Results — Launch Roadmap (NOT_STARTED) */}
          {isResults && launch && (
            <div ref={resultsRef} className="max-w-5xl mx-auto space-y-8" data-testid="launch-results">
              {/* Header */}
              <div className="bg-gradient-to-br from-gold/20 to-white/5 border border-gold/30 rounded-2xl p-6 md:p-8">
                <p className="text-gold font-heading text-xs md:text-sm font-bold tracking-widest uppercase mb-2">Your Business Launch Roadmap</p>
                <p className="text-white text-base md:text-lg mb-4">
                  Everything you need to start your <span className="text-gold font-semibold">{labelFor(INDUSTRIES, answers.industry)}</span> business legally in Coimbatore
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs md:text-sm">
                  <SummaryCell label="Business Type" value={labelFor(BUSINESS_TYPES, answers.businessType)} />
                  <SummaryCell label="Industry" value={labelFor(INDUSTRIES, answers.industry)} />
                  <SummaryCell label="Turnover" value="Pre-Revenue (Planning Stage)" />
                  <SummaryCell label="Employees" value={labelFor(EMPLOYEES, answers.employees)} />
                </div>
                <button
                  onClick={restart}
                  className="no-print mt-5 inline-flex items-center gap-2 text-gold text-sm font-medium hover:underline"
                  data-testid="restart-btn"
                >
                  <RotateCcw size={14} /> Start over
                </button>
              </div>

              {/* Startup Readiness Score */}
              <div className="rounded-2xl p-6 md:p-8 text-center border-2 bg-amber-500/10 border-amber-500/50" data-testid="readiness-score-box">
                <p className="text-slate-300 text-xs font-bold tracking-widest uppercase mb-3">Your Startup Readiness Score</p>
                <div className="inline-flex items-center justify-center w-28 h-28 rounded-full mb-4 bg-amber-500/20 border-4 border-amber-500">
                  <ShieldCheck className="text-amber-400" size={44} />
                </div>
                <p className="text-2xl md:text-3xl font-bold font-heading text-amber-400" data-testid="readiness-level">
                  🟡 NOT YET COMPLIANT
                </p>
                <p className="text-white mt-2 text-base md:text-lg">
                  <span className="font-bold text-gold">{launch.remainingSteps}</span> steps to complete before you can legally operate
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6 text-left max-w-3xl mx-auto">
                  <div className="bg-navy/60 rounded-lg p-4">
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Estimated setup cost</p>
                    <p className="text-white font-semibold">₹{formatINR(launch.setupCost.totalMin)}–₹{formatINR(launch.setupCost.totalMax)}</p>
                  </div>
                  <div className="bg-navy/60 rounded-lg p-4">
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Estimated timeline</p>
                    <p className="text-white font-semibold">2–4 weeks with expert support</p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Penalty if you skip compliance</p>
                    <p className="text-red-400 font-semibold">₹{formatINR(launch.penaltyRiskNoCompliance)}+/year</p>
                  </div>
                </div>
              </div>

              {/* Phases */}
              {launch.phases.map((phase, pi) => (
                <div
                  key={phase.title}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8"
                  data-testid={`launch-phase-${pi + 1}`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-full bg-gold text-navy font-bold flex items-center justify-center">{pi + 1}</div>
                    <div>
                      <h2 className="text-xl md:text-2xl font-semibold font-heading text-white">{phase.title}</h2>
                      <p className="text-slate-400 text-sm">{phase.subtitle}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {phase.items.map((item, i) => (
                      <div
                        key={i}
                        className={`bg-navy/60 border-l-4 ${item.completed ? "border-green-500" : "border-gold"} rounded-lg px-5 py-4`}
                        data-testid={`launch-phase-${pi + 1}-item-${i}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded-[4px] border-2 flex items-center justify-center ${
                            item.completed ? "bg-green-500 border-green-500 text-navy" : "border-slate-400"
                          }`}>
                            {item.completed && <CheckCircle2 size={14} />}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white font-semibold text-base">{item.title}</p>
                              {item.completed && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/40">
                                  ✓ Done
                                </span>
                              )}
                            </div>
                            <p className="text-slate-300 text-sm mt-1">{item.note}</p>
                            {item.link && !item.completed && (
                              <Link to={item.link} className="text-gold text-xs font-medium hover:underline mt-2 inline-flex items-center gap-1 no-print">
                                Learn more <ArrowRight size={12} />
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Cost Section */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8" data-testid="launch-cost-section">
                <div className="flex items-center gap-3 mb-5">
                  <IndianRupee className="text-gold" size={24} />
                  <h2 className="text-xl md:text-2xl font-semibold text-white font-heading">Complete Business Launch Package</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mb-5">
                  <CostCard label="Government fees" value={`₹${formatINR(launch.setupCost.govMin)}–₹${formatINR(launch.setupCost.govMax)}`} sub="one-time" />
                  <CostCard label="Professional fees" value={`₹${formatINR(launch.setupCost.profMin)}–₹${formatINR(launch.setupCost.profMax)}`} sub="one-time" />
                  <CostCard label="Penalty risk without compliance" value={`₹${formatINR(launch.penaltyRiskNoCompliance)}+`} sub="per year" danger />
                </div>
                <div className="bg-navy/60 border border-gold/40 rounded-xl p-5 md:p-6 text-center">
                  <p className="text-slate-300 text-xs font-bold tracking-widest uppercase mb-2">Total setup cost</p>
                  <p className="text-2xl md:text-3xl font-bold text-gold font-heading mb-3">
                    ₹{formatINR(launch.setupCost.totalMin)}–₹{formatINR(launch.setupCost.totalMax)}
                  </p>
                  <p className="text-white text-sm md:text-base">
                    Starting compliant costs ~<span className="text-green-400 font-semibold">₹15,000</span>.
                    Starting <span className="text-red-400 font-semibold">without compliance</span> costs <span className="text-red-400 font-semibold">₹2,00,000+</span> in penalties.
                    <span className="block mt-2 text-gold font-semibold">The choice is clear.</span>
                  </p>
                </div>
              </div>

              {/* Primary CTA */}
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-2xl p-6 md:p-8 no-print" data-testid="launch-primary-cta">
                <div className="flex flex-col gap-4">
                  <div>
                    <p className="text-navy font-semibold text-base md:text-lg mb-2">
                      Starting a business is exciting. Compliance shouldn't slow you down.
                    </p>
                    <p className="text-navy/80 text-sm md:text-base mb-1">
                      We handle ALL registrations — you focus on your business.
                    </p>
                    <p className="text-navy/80 text-sm md:text-base">
                      <strong>Complete Business Launch Package</strong> — all registrations + 1 year compliance support. One expert. One WhatsApp message. Everything handled.
                    </p>
                  </div>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp self-start whitespace-nowrap"
                    data-testid="cta-whatsapp-btn"
                  >
                    <MessageCircle size={18} /> Launch My Business — WhatsApp Us
                  </a>
                </div>
              </div>

              {/* Secondary CTA: PDF */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 no-print">
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 border-2 border-gold text-gold hover:bg-gold hover:text-navy font-semibold px-6 py-3 rounded-lg transition-colors"
                  data-testid="download-pdf-btn"
                >
                  <Download size={18} /> Download Your Startup Checklist as PDF
                </button>
                <span className="text-slate-400 text-xs max-w-sm text-center">
                  Opens print dialog — choose "Save as PDF" as the destination
                </span>
              </div>

              {/* Links Footer — 3 grids: Services / Calculators / Templates */}
              <div className="space-y-8 no-print" data-testid="launch-links-footer">
                {/* Service Links */}
                <div data-testid="launch-service-links">
                  <p className="text-gold text-xs font-bold tracking-widest uppercase mb-3">Related Services</p>
                  <div className="grid md:grid-cols-3 gap-3">
                    <ServiceLink to="/business-registration" label="View Business Registration Services" />
                    <ServiceLink to="/gst-registration" label="View GST Registration Services" />
                    <ServiceLink to="/itr-filing" label="View ITR Filing Services" />
                  </div>
                </div>

                {/* Calculator Links — horizontal scroll on mobile, 3-col grid on desktop */}
                <div data-testid="launch-calculator-links">
                  <p className="text-gold text-xs font-bold tracking-widest uppercase mb-3">Free Calculators</p>
                  <div className="md:grid md:grid-cols-3 md:gap-3 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 md:overflow-visible md:snap-none">
                    {[
                      { to: "/gst-penalty-calculator", label: "GST Late Fee & Penalty Calculator" },
                      { to: "/gst-interest-calculator", label: "GST Interest Calculator" },
                      { to: "/income-tax-calculator", label: "Income Tax Calculator" },
                      { to: "/tds-penalty-calculator", label: "TDS Penalty Calculator" },
                      { to: "/loan-eligibility-calculator", label: "Business Loan Eligibility Calculator" },
                      { to: "/emi-calculator", label: "EMI Calculator" },
                    ].map((c) => (
                      <div key={c.to} className="snap-start flex-shrink-0 w-[85%] md:w-auto">
                        <ServiceLink to={c.to} label={c.label} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Template Links */}
                <div data-testid="launch-template-links">
                  <p className="text-gold text-xs font-bold tracking-widest uppercase mb-3">Free Templates</p>
                  <div className="grid md:grid-cols-3 gap-3">
                    <ServiceLink to="/itr-checklist" label="ITR Checklist Template" />
                    <ServiceLink to="/gst-notice-response-template" label="GST Notice Response Template" />
                    <ServiceLink to="/gst-reconciliation-worksheet" label="GST Reconciliation Worksheet" />
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <p className="text-slate-400 text-xs max-w-4xl">
                <strong className="text-slate-200">Disclaimer:</strong> This launch roadmap is generated based on general compliance requirements under Indian tax and business laws as of April 2026. Actual requirements may vary based on specific business circumstances, state-level regulations, and recent amendments. This tool provides guidance only — consult a qualified compliance expert for advice specific to your business.
              </p>
            </div>
          )}

          {/* Results — Compliance Roadmap (existing businesses) */}
          {isResults && roadmap && (
            <div ref={resultsRef} className="max-w-5xl mx-auto space-y-8" data-testid="roadmap-results">
              {/* Header Card */}
              <div className="bg-gradient-to-br from-gold/20 to-white/5 border border-gold/30 rounded-2xl p-6 md:p-8">
                <p className="text-gold font-heading text-xs md:text-sm font-bold tracking-widest uppercase mb-2">Your Business Compliance Roadmap</p>
              {/* Turnover label for display uses real label for Pre-Revenue too */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs md:text-sm">
                  <SummaryCell label="Business Type" value={labelFor(BUSINESS_TYPES, answers.businessType)} />
                  <SummaryCell label="Industry" value={labelFor(INDUSTRIES, answers.industry)} />
                  <SummaryCell
                    label="Turnover"
                    value={answers.businessType === "NOT_STARTED" ? "Pre-Revenue (Planning Stage)" : labelFor(TURNOVERS, answers.turnover)}
                  />
                  <SummaryCell label="Employees" value={labelFor(EMPLOYEES, answers.employees)} />
                </div>
                <button
                  onClick={restart}
                  className="no-print mt-5 inline-flex items-center gap-2 text-gold text-sm font-medium hover:underline"
                  data-testid="restart-btn"
                >
                  <RotateCcw size={14} /> Start over
                </button>
              </div>

              {/* Risk Score */}
              <div
                className={`rounded-2xl p-6 md:p-8 text-center border-2 ${
                  roadmap.riskColor === "red"
                    ? "bg-red-500/10 border-red-500/50"
                    : roadmap.riskColor === "amber"
                    ? "bg-amber-500/10 border-amber-500/50"
                    : "bg-green-500/10 border-green-500/50"
                }`}
                data-testid="risk-score-box"
              >
                <p className="text-slate-300 text-xs font-bold tracking-widest uppercase mb-3">Your Compliance Risk Score</p>
                <div className={`inline-flex items-center justify-center w-28 h-28 rounded-full mb-4 ${
                  roadmap.riskColor === "red" ? "bg-red-500/20 border-4 border-red-500" :
                  roadmap.riskColor === "amber" ? "bg-amber-500/20 border-4 border-amber-500" :
                  "bg-green-500/20 border-4 border-green-500"
                }`}>
                  {roadmap.fullyCompliant ? (
                    <CheckCircle2 className="text-green-400" size={52} />
                  ) : (
                    <AlertTriangle
                      className={roadmap.riskColor === "red" ? "text-red-400" : roadmap.riskColor === "amber" ? "text-amber-400" : "text-green-400"}
                      size={44}
                    />
                  )}
                </div>
                <p className={`text-3xl md:text-4xl font-bold font-heading ${
                  roadmap.riskColor === "red" ? "text-red-400" : roadmap.riskColor === "amber" ? "text-amber-400" : "text-green-400"
                }`} data-testid="risk-level">
                  {roadmap.fullyCompliant ? "🟢 FULLY COMPLIANT — Great job!" : roadmap.riskLabel}
                </p>
                <p className="text-white mt-3">Estimated penalty exposure: <span className="font-semibold">{roadmap.penaltyEstimate}</span></p>
                <p className="text-slate-300 text-sm mt-1">
                  {roadmap.fullyCompliant ? (
                    <>All <span className="font-bold text-gold">{roadmap.totalMandatory}</span> mandatory registrations are complete. ~<span className="font-bold text-gold">{roadmap.filingsCount}</span> filings to stay current this year.</>
                  ) : (
                    <>
                      <span className="font-bold text-gold">{roadmap.missingMandatory}</span> of <span className="font-bold text-gold">{roadmap.totalMandatory}</span> mandatory registrations missing
                      {roadmap.completedMandatory > 0 && <> · <span className="font-bold text-green-400">{roadmap.completedMandatory}</span> already completed</>}
                      {" "}· ~<span className="font-bold text-gold">{roadmap.filingsCount}</span> filings this year
                    </>
                  )}
                </p>
              </div>

              {/* Mandatory */}
              <RoadmapSection
                title="Mandatory — Do These Immediately"
                subtitle={
                  roadmap.totalMandatory === 0
                    ? "No mandatory registrations triggered for your business profile"
                    : `${roadmap.missingMandatory} missing · ${roadmap.completedMandatory} completed of ${roadmap.totalMandatory} mandatory items`
                }
                items={roadmap.mandatory}
                color="red"
                emoji="🔴"
                testId="mandatory-section"
                showActionBadge
              />

              {/* Recommended */}
              <RoadmapSection
                title="Recommended — Within 30 Days"
                subtitle="Strongly advised to strengthen your business"
                items={roadmap.recommended}
                color="amber"
                emoji="🟡"
                testId="recommended-section"
              />

              {/* Optional */}
              <RoadmapSection
                title="Optional — Plan for This Year"
                subtitle="Nice to have, improves long-term position"
                items={roadmap.optional}
                color="green"
                emoji="🟢"
                testId="optional-section"
              />

              {/* Calendar */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8" data-testid="calendar-section">
                <div className="flex items-center gap-3 mb-5">
                  <Calendar className="text-gold" size={24} />
                  <h2 className="text-xl md:text-2xl font-semibold text-white font-heading">Your Annual Compliance Calendar</h2>
                </div>

                <CalendarBucket label="Monthly" items={roadmap.calendar.monthly} />
                <CalendarBucket label="Quarterly" items={roadmap.calendar.quarterly} />
                <CalendarBucket label="Annually" items={roadmap.calendar.annually} />
              </div>

              {/* Cost */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8" data-testid="cost-section">
                <div className="flex items-center gap-3 mb-5">
                  <IndianRupee className="text-gold" size={24} />
                  <h2 className="text-xl md:text-2xl font-semibold text-white font-heading">Estimated Annual Compliance Cost</h2>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-5">
                  <CostCard label="Government fees" value={`₹${formatINR(roadmap.cost.govMin)}–₹${formatINR(roadmap.cost.govMax)}`} sub="per year" />
                  <CostCard label="Professional fees" value={`₹${formatINR(roadmap.cost.profMin)}–₹${formatINR(roadmap.cost.profMax)}`} sub="per year" />
                  <CostCard label="Penalty risk if non-compliant" value={`₹${formatINR(roadmap.cost.penaltyRisk)}+`} sub="per year" danger />
                </div>

                <div className="bg-navy/60 border border-gold/40 rounded-xl p-5 md:p-6 text-center">
                  <p className="text-slate-300 text-xs font-bold tracking-widest uppercase mb-2">Total estimated cost</p>
                  <p className="text-2xl md:text-3xl font-bold text-gold font-heading mb-3">
                    ₹{formatINR(roadmap.cost.totalMin)}–₹{formatINR(roadmap.cost.totalMax)}/year
                  </p>
                  <p className="text-white text-sm md:text-base">
                    <span className="text-green-400 font-semibold">Compliance cost</span> ₹{formatINR(roadmap.cost.totalMin)}–₹{formatINR(roadmap.cost.totalMax)}/yr
                    <span className="mx-2 text-slate-500">vs</span>
                    <span className="text-red-400 font-semibold">Penalty risk</span> ₹{formatINR(roadmap.cost.penaltyRisk)}+/yr
                    <span className="block mt-2 text-gold font-semibold">Compliance is ~{roadmap.cost.multiplier}x cheaper than penalties</span>
                  </p>
                </div>
              </div>

              {/* Primary CTA */}
              <div className={`${roadmap.fullyCompliant ? "bg-green-50 border-green-500" : "bg-amber-50 border-amber-500"} border-l-4 rounded-2xl p-6 md:p-8 no-print`} data-testid="primary-cta">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
                  {roadmap.fullyCompliant ? (
                    <CheckCircle2 className="text-green-600 flex-shrink-0" size={40} />
                  ) : (
                    <AlertTriangle className="text-amber-600 flex-shrink-0" size={40} />
                  )}
                  <div className="flex-1">
                    <p className="text-navy font-semibold text-base md:text-lg mb-1">
                      {roadmap.fullyCompliant
                        ? "All core registrations are in place — excellent!"
                        : `Your business has ${roadmap.missingMandatory} compliance gap${roadmap.missingMandatory === 1 ? "" : "s"}.`}
                    </p>
                    <p className="text-navy/80 text-sm md:text-base">
                      {roadmap.fullyCompliant
                        ? "Let our experts handle your ongoing monthly, quarterly and annual filings so you stay fully compliant year-round."
                        : "Our experts will handle everything — registrations, filings, and ongoing compliance. One call. Complete peace of mind."}
                    </p>
                  </div>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp flex-shrink-0 whitespace-nowrap"
                    data-testid="cta-whatsapp-btn"
                  >
                    <MessageCircle size={18} /> {roadmap.fullyCompliant ? "Manage Ongoing Compliance" : "Fix My Compliance"} — WhatsApp Us
                  </a>
                </div>
              </div>

              {/* Secondary CTA: PDF */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 no-print">
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 border-2 border-gold text-gold hover:bg-gold hover:text-navy font-semibold px-6 py-3 rounded-lg transition-colors"
                  data-testid="download-pdf-btn"
                >
                  <Download size={18} /> Download Your Roadmap as PDF
                </button>
                <span className="text-slate-400 text-xs max-w-sm text-center">
                  Opens print dialog — choose "Save as PDF" as the destination
                </span>
              </div>

              {/* Service links */}
              <div className="grid md:grid-cols-3 gap-3 no-print">
                <ServiceLink to="/gst-registration" label="View our GST Registration Services" />
                <ServiceLink to="/itr-filing" label="View our ITR Filing Services" />
                <ServiceLink to="/business-registration" label="View our Business Registration Services" />
              </div>

              {/* Disclaimer */}
              <p className="text-slate-400 text-xs max-w-4xl">
                <strong className="text-slate-200">Disclaimer:</strong> This roadmap is generated based on general compliance requirements under Indian tax and business laws as of April 2026. Actual requirements may vary based on specific business circumstances, state-level regulations, and recent amendments. This tool provides guidance only — consult a qualified compliance expert for advice specific to your business. Wintrust Solutions is not liable for any penalties arising from reliance on this tool alone.
              </p>
            </div>
          )}
        </div>
      </section>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          header, footer { display: none !important; }
          body, section { background: white !important; color: black !important; }
          .bg-navy, .bg-white\\/5 { background: white !important; }
          .text-white, .text-slate-200, .text-slate-300, .text-slate-400 { color: #0a1f44 !important; }
          .text-gold { color: #b08a2c !important; }
        }
      `}</style>
    </>
  );
};

// ---------- Helpers ----------
const SummaryCell = ({ label, value }) => (
  <div>
    <p className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold mb-1">{label}</p>
    <p className="text-white font-medium">{value}</p>
  </div>
);

const RoadmapSection = ({ title, subtitle, items, color, emoji, testId, showActionBadge }) => {
  if (!items || items.length === 0) return null;
  const defaultBorderColor = color === "red" ? "border-red-500" : color === "amber" ? "border-amber-500" : "border-green-500";
  const badgeColor = color === "red" ? "text-red-400" : color === "amber" ? "text-amber-400" : "text-green-400";
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8" data-testid={testId}>
      <div className="mb-5">
        <h2 className={`text-xl md:text-2xl font-semibold font-heading ${badgeColor} flex items-center gap-2`}>
          <span>{emoji}</span> <span>{title}</span>
        </h2>
        <p className="text-slate-400 text-sm mt-1">{subtitle}</p>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => {
          const borderClass = item.completed ? "border-green-500" : defaultBorderColor;
          return (
            <div
              key={i}
              className={`bg-navy/60 border-l-4 ${borderClass} rounded-lg px-5 py-4`}
              data-testid={`${testId}-item-${i}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.completed && <CheckCircle2 className="text-green-400 flex-shrink-0" size={18} />}
                    <p className="text-white font-semibold text-base">{item.title}</p>
                    {item.completed ? (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/40" data-testid={`${testId}-badge-${i}`}>
                        ✓ Completed
                      </span>
                    ) : showActionBadge ? (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/40" data-testid={`${testId}-badge-${i}`}>
                        🔴 Action Needed
                      </span>
                    ) : null}
                  </div>
                  <p className="text-slate-300 text-sm mt-1">{item.note}</p>
                  {item.link && !item.completed && (
                    <Link to={item.link} className="text-gold text-xs font-medium hover:underline mt-2 inline-flex items-center gap-1 no-print">
                      Learn more <ArrowRight size={12} />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CalendarBucket = ({ label, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-5 last:mb-0">
      <p className="text-gold text-xs font-bold tracking-widest uppercase mb-2">{label}</p>
      <div className="bg-navy/60 rounded-lg overflow-hidden border border-white/5">
        {items.map((it, i) => (
          <div
            key={i}
            className={`flex justify-between gap-4 px-5 py-3 ${i % 2 ? "bg-white/[0.02]" : ""} ${i < items.length - 1 ? "border-b border-white/5" : ""}`}
          >
            <span className="text-slate-200 text-sm">{it.task}</span>
            <span className="text-gold text-sm font-medium whitespace-nowrap">{it.deadline}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CostCard = ({ label, value, sub, danger }) => (
  <div className={`rounded-xl p-5 border ${danger ? "bg-red-500/5 border-red-500/30" : "bg-navy/60 border-white/10"}`}>
    <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold mb-2">{label}</p>
    <p className={`text-lg md:text-xl font-bold ${danger ? "text-red-400" : "text-white"} font-heading`}>{value}</p>
    <p className="text-slate-500 text-xs mt-1">{sub}</p>
  </div>
);

const ServiceLink = ({ to, label }) => (
  <Link
    to={to}
    className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold/40 rounded-xl px-5 py-4 text-slate-200 hover:text-gold text-sm font-medium inline-flex items-center justify-between gap-2 transition-colors"
  >
    <span>{label}</span>
    <ArrowRight size={16} />
  </Link>
);

export default ComplianceRoadmapPage;
